import React, { useCallback, useContext, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';

import DataState from 'app/src/components/DataState';
import { CREATE_FEE } from 'app/src/components/Fees/queries';
import AuthContext from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import {
  Fields,
  defaultValues,
  prepareInput,
} from 'app/src/elements/hookForms';
import { currency } from 'app/src/utils/stringHelpers';

export const FIELDS = {
  type: {
    label: 'Type',
    select: [],
    rules: { required: true },
  },
  amount: {
    label: 'Amount',
    rules: {
      required: true,
      maxLength: { value: 8, message: 'Must be less than $1,000,000' },
      validate: {
        matchPattern: (v) =>
          /\d{1,10}(\.{1}\d{1,2}){0,1}?$/.test(v) ||
          "Please use number only without commas",
      },
    },
  },
  description: {
    label: 'Description',
  },
};

export const amountData = feeMarkup => {
  if ('percent' === feeMarkup.markupType) {
    return `${feeMarkup.amount}%`;
  } else {
    return `${currency(feeMarkup.amount)}`;
  }
};

export const calculateAmount = (amount, feeMarkup) => {
  if (!amount) { return null; }

  const expenseAmount = parseFloat(amount);

  if (!feeMarkup || !expenseAmount) { return expenseAmount; }

  const markupAmount = parseFloat(feeMarkup.amount);

  if ('percent' === feeMarkup.markupType) {
    return Math.round(expenseAmount * ((100 + markupAmount) / 100), 2);
  } else {
    return expenseAmount + markupAmount;
  }
};

const NewFee = ({ asset, saleAssetId, expenseAmount, onCompleted, txn }) => {
  const { feeTypes, settings } = useContext(AuthContext);

  const [createFee] = useMutation(CREATE_FEE, { onCompleted: onCompleted });

  const {
    control, handleSubmit, watch, setValue, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({ fields: FIELDS })
  });

  const values = watch();

  const onSubmit = input => {
    createFee({
      variables: {
        ...input,
        buyerFee: ('Invoice' === txn?.type),
        assetId: asset.id,
        invoiceId: ('Invoice' === txn?.type ? txn.id : null),
        saleAssetId
      }
    });
  };

  const feeMarkups = feeTypes.map(feeType => (
    asset?.seller.feeMarkups.find(entry => entry.feeType === feeType.name) ||
      settings?.feeMarkups.find(entry => entry.feeType === feeType.name) ||
      {
        feeType: feeType.name,
        markupType: 'percent',
        amount: '0',
        defaultMarkup: true,
      }
  ));

  const feeMarkup = feeMarkups.find(entry => entry.feeType === values.type)
  const suggestedAmount = calculateAmount(expenseAmount, feeMarkup);

  useEffect(() => {
    if (suggestedAmount) {
      setValue('amount', suggestedAmount);
    }
  }, [suggestedAmount]);

  if (!settings || !asset) { return <DataState.Loading />; }

  const fields = { ...FIELDS };

  fields.type.select = feeTypes;

  if (feeMarkup) {
    const markupDescription = [
      'Markup: ',
      ('percent' === feeMarkup.markupType ? 'Percent' : 'Flat'),
      amountData(feeMarkup),
      (feeMarkup.defaultAmount ? '(System Default)' : ''),
    ].join(' ');

    fields.amount.hint = markupDescription;
  } else {
    fields.amount.hint = null;
  }

  return (
    <View>
      <Fields
        fields={fields}
        control={control}
        errors={errors}
      />

      <ButtonGroup>
        <Button label="Create Fee" onPress={handleSubmit(onSubmit)} />
      </ButtonGroup>
    </View>
  );
};

export default NewFee;
